// Missing observations must never become zero through Number(null).
const observedNumber = v => v == null || v === "" || typeof v === "boolean" ? NaN : Number(v);

export const VIEW_CONFIG = {
  actual: { label: "Real", position: "position", prefix: "" },
  expected: {
    label: "Expected",
    position: "expected_position",
    prefix: "expected_",
  },
  should: { label: "Should", position: "should_position", prefix: "should_" },
  market: { label: "Markt", position: "market_position", prefix: "market_" },
};

export function prepareCompactLeagueRows(rows, view, teamNames = new Map()) {
  return prepareLeagueRowsForScope(rows, view, "overall", teamNames);
}

export function prepareLeagueRowsForScope(rows, view, scope = "overall", teamNames = new Map()) {
  const config = VIEW_CONFIG[view] || VIEW_CONFIG.actual;
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const values = row?.[scope] || {};
      const read = (field) => observedNumber(values[`${config.prefix}${field}`]);
      const goalsScored = read("goals_scored");
      const goalsAgainst = read("goals_against");
      const played = read("games_played");
      const goalSum = goalsScored + goalsAgainst;
      const scopedRate = (scope, field) => {
        const scopedValues = row?.[scope] || {};
        const games = observedNumber(scopedValues[`${config.prefix}games_played`]);
        const value = observedNumber(scopedValues[`${config.prefix}${field}`]);
        return games > 0 && Number.isFinite(value) ? value / games : null;
      };
      const forHome = scopedRate("home", "goals_scored");
      const againstHome = scopedRate("home", "goals_against");
      const forAway = scopedRate("away", "goals_scored");
      const againstAway = scopedRate("away", "goals_against");
      const forTotal = Number.isFinite(forHome) && Number.isFinite(forAway)
        ? (forHome + forAway) / 2
        : null;
      const againstTotal = Number.isFinite(againstHome) && Number.isFinite(againstAway)
        ? (againstHome + againstAway) / 2
        : null;
      const scopedSummary = (scope) => {
        const scopedValues = row?.[scope] || {};
        const value = (field) => observedNumber(scopedValues[`${config.prefix}${field}`]);
        const scopedGoalsScored = value("goals_scored");
        const scopedGoalsAgainst = value("goals_against");
        const storedDifference = value("goal_difference");
        return {
          played: value("games_played"),
          points: value("points"),
          won: value("won"), draw: value("draw"), lost: value("lost"),
          goalsScored: scopedGoalsScored,
          goalsAgainst: scopedGoalsAgainst,
          difference: Number.isFinite(storedDifference) ? storedDifference : Number.isFinite(scopedGoalsScored) && Number.isFinite(scopedGoalsAgainst) ? scopedGoalsScored - scopedGoalsAgainst : null,
        };
      };
      return {
        teamId: Number(row?.team_id),
        name:
          teamNames.get(Number(row?.team_id)) ||
          row?.team_name ||
          `Team ${row?.team_id}`,
        position: Number(row?.[config.position]),
        played,
        won: read("won"),
        draw: read("draw"),
        lost: read("lost"),
        goalsScored,
        goalsAgainst,
        goalSum,
        goalAverage: played > 0 ? goalSum / played : null,
        difference: read("goal_difference"),
        quotient:
          goalsAgainst === 0
            ? Infinity
            : goalsScored / goalsAgainst,
        points: read("points"),
        home: scopedSummary("home"),
        away: scopedSummary("away"),
        fixtureSettingValues: {
          total_strength:
            Number.isFinite(forTotal) && Number.isFinite(againstTotal) && againstTotal > 0
              ? forTotal / againstTotal : null,
          total_goals:
            Number.isFinite(forTotal) && Number.isFinite(againstTotal)
              ? forTotal + againstTotal : null,
          for_total: forTotal,
          against_total: againstTotal,
          for_home: forHome,
          against_home: againstHome,
          for_away: forAway,
          against_away: againstAway,
          for_homefactor:
            Number.isFinite(forHome) && Number.isFinite(forAway) && (forHome + forAway) > 0
              ? forHome / ((forHome + forAway) / 2)
              : null,
          against_homefactor:
            Number.isFinite(againstHome) && Number.isFinite(againstAway) && (againstHome + againstAway) > 0
              ? againstHome / ((againstHome + againstAway) / 2)
              : null,
          draw_factor: null,
        },
      };
    })
    .sort((a, b) =>
      (Number.isFinite(b.points) ? b.points : -Infinity) -
        (Number.isFinite(a.points) ? a.points : -Infinity) ||
      (Number.isFinite(b.difference) ? b.difference : -Infinity) -
        (Number.isFinite(a.difference) ? a.difference : -Infinity) ||
      (Number.isFinite(b.goalsScored) ? b.goalsScored : -Infinity) -
        (Number.isFinite(a.goalsScored) ? a.goalsScored : -Infinity) ||
      a.name.localeCompare(b.name)
    );
}
